import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Shield, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotDialog, setForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [autoLoggingIn, setAutoLoggingIn] = useState(true);
  const { login, user, token, handleInstallClick } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);

  // Auto-login if user is already authenticated
  useEffect(() => {
    if (user && token) {
      // User is already logged in, redirect to appropriate page
      if (user.role === 'admin') navigate('/dashboard', { replace: true });
      else if (user.role === 'technician') navigate('/technician', { replace: true });
      else navigate('/client', { replace: true });
    }
  }, [user, token, navigate]);

  // Check for saved credentials and auto-login on mount
  useEffect(() => {
    const performAutoLogin = async () => {
      const savedEmail = localStorage.getItem('rememberedEmail');
      const savedPassword = localStorage.getItem('rememberedPassword');
      const existingToken = localStorage.getItem('token');
      
      // If already has token, AuthProvider will handle it
      if (existingToken) {
        setAutoLoggingIn(false);
        return;
      }
      
      // If has saved credentials, auto-login
      if (savedEmail && savedPassword) {
        setLoginData({ email: savedEmail, password: savedPassword });
        setRememberMe(true);
        
        try {
          const userData = await login(savedEmail, savedPassword);
          toast.success('Welcome back!');
          
          if (userData.role === 'admin') navigate('/dashboard', { replace: true });
          else if (userData.role === 'technician') navigate('/technician', { replace: true });
          else navigate('/client', { replace: true });
          return;
        } catch (error) {
          // Auto-login failed, clear saved credentials
          console.log('Auto-login failed:', error);
          localStorage.removeItem('rememberedEmail');
          localStorage.removeItem('rememberedPassword');
        }
      }
      
      setAutoLoggingIn(false);
    };
    
    performAutoLogin();
  }, [login, navigate]);

  // Show loading while checking auto-login
  if (autoLoggingIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b1f3a]">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-white shadow-xl overflow-hidden mx-auto mb-5 ring-4 ring-white/10">
            <img src="/logo.jpg" alt="Hayat Security" className="w-full h-full object-cover" />
          </div>
          <div className="w-8 h-8 border-[3px] border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-slate-300 text-sm">Signing you in…</p>
        </div>
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      
      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', loginData.email);
        localStorage.setItem('rememberedPassword', loginData.password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }
      
      toast.success('Login successful!');
      
      if (user.role === 'admin') navigate('/dashboard');
      else if (user.role === 'technician') navigate('/technician');
      else navigate('/client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If this email exists, a temporary password will be sent to your registered phone via WhatsApp');
      setForgotDialog(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Failed to process request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      {/* Brand panel */}
      <div className="relative overflow-hidden bg-[#0b1f3a] text-white lg:w-[46%] xl:w-1/2 px-6 pt-8 pb-10 sm:px-10 lg:p-14 flex flex-col">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-blue-600/25 blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }}></div>

        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white overflow-hidden shadow-lg ring-2 ring-white/20 flex-shrink-0">
            <img src="/logo.jpg" alt="Hayat Security" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-lg sm:text-xl font-black tracking-tight leading-tight">Hayat Security</div>
            <div className="text-[11px] sm:text-xs text-cyan-300 font-mono tracking-wide">CCTV Management System</div>
          </div>
        </div>

        <div className="relative mt-8 lg:mt-auto lg:mb-auto max-w-md">
          <p className="text-cyan-300 text-xs font-semibold uppercase tracking-[0.2em] mb-3">Think Security. Think Hayat.</p>
          <h1 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black leading-[1.1] tracking-tight">
            Run jobs, billing &amp; accounts<br className="hidden sm:block" /> from one place.
          </h1>
          <ul className="hidden lg:grid gap-3 mt-8 text-sm text-slate-300">
            {[
              ['Service jobs & site visits', 'Assign technicians, track every job live'],
              ['GST billing & accounts', 'Invoices, quotations, purchase, ledgers & reports'],
              ['Team & attendance', 'Attendance, expenses and WhatsApp updates'],
            ].map(([t, d]) => (
              <li key={t} className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0"></span>
                <span><span className="text-white font-semibold">{t}</span><br />{d}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative hidden lg:flex items-center gap-2 text-xs text-slate-400 mt-8">
          <Shield className="w-4 h-4 text-cyan-400" /> Secure login · Hayat Enterprises, Nagpur
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-4 sm:px-6 -mt-6 lg:mt-0 pb-10 lg:py-10 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl lg:shadow-none border border-slate-200 lg:border-0 lg:bg-transparent p-6 sm:p-8 lg:p-0">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-slate-700 text-sm font-medium">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="you@hayatcctv.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="pl-10 h-12 bg-white border-slate-300 focus-visible:ring-cyan-500 focus:border-cyan-500 text-slate-800 text-base rounded-xl"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-slate-700 text-sm font-medium">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="pl-10 h-12 bg-white border-slate-300 focus-visible:ring-cyan-500 focus:border-cyan-500 text-slate-800 text-base rounded-xl"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-slate-300 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                  />
                  <label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer select-none">Remember me</label>
                </div>
                <button type="button" onClick={() => setForgotDialog(true)} className="text-sm font-medium text-cyan-700 hover:text-cyan-800">
                  Forgot password?
                </button>
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full h-12 rounded-xl bg-[#0b1f3a] hover:bg-[#12305a] text-white text-base font-semibold shadow-lg shadow-slate-900/10"
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-slate-200 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
              {isMobileDevice && (
                <button type="button" onClick={handleInstallClick} className="text-cyan-700 hover:text-cyan-800 font-medium" data-testid="install-app-button">
                  📱 Install App on Your Phone
                </button>
              )}
              <a href="https://www.hayatcctv.com" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:text-cyan-800 font-medium" data-testid="go-to-website-button">
                🌐 Go to Website
              </a>
            </div>
          </div>
          <p className="text-center text-[11px] text-slate-400 mt-6 lg:hidden flex items-center justify-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Secure login · Hayat Enterprises, Nagpur</p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotDialog} onOpenChange={setForgotDialog}>
        <DialogContent className="bg-slate-50 border-slate-200 text-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Forgot Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter your email address and we'll send a temporary password to your registered phone number via WhatsApp.
            </p>
            <div>
              <Label className="text-slate-700">Email</Label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-white border-slate-300 mt-1"
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setForgotDialog(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-500" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;